<template>
  <div class="asset-drawer__form">
    <UIPanel
      v-model="clearedSearch"
      v-model:storage="searchStorage"
      placeholder="Search positions..."
    >
      <template #default="{ ButtonExport }">
        <component
          :is="ButtonExport"
          :disabled="isExportDisabled"
          @click="handleClickExport"
        />
      </template>
    </UIPanel>
  </div>
  <UIGridSkeleton
    v-if="isLoading"
    :columns
    :footer
    :sm
    message="Loading positions..."
  />
  <UIGrid
    v-else
    v-model:sort="sort"
    :columns
    :items
    :footer
    :sm
    scrollable
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { sumBy } from 'lodash'

import {
  AnalyticsField,
  AssetClass,
  FilterComparison,
  FilterGroup,
  FilterLogic,
  Sort,
} from '@types'
import { AnalyticsPosition } from '@/store/analytics/position/utils/types'

import { ANALYTICS_CAPTIONS, ASSET_FIELD } from '@/const'

import { getAssetName, getAssetTicker } from '@/views/Analytics/utils/helpers'
import { handleCatchedError } from '@/helpers/common'

import useGridSort from '@/components/hooks/gridSort'
import useGridFilter from '@/components/hooks/gridFilter'
import { useHandledGridExport } from '@/components/hooks/gridExport'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useRepositoriesStore } from '@/store/repositories/index'

import { UIPanel } from '@ui/core'
import { UIGrid, UIGridSkeleton } from '@ui/grid'

type Props = {
  instance: AssetClass
}

const props = defineProps<Props>()

defineOptions({ inheritAttrs: false })

const analyticsStore = useAnalyticsStore()
const assetsBunchStore = useAssetsBunchStore()
const repositoriesStore = useRepositoriesStore()

const isLoading = ref(true)
const positions = ref<AnalyticsPosition[]>([])

const searchStorage = ref<string>()
const clearedSearch = ref<string>()
const sort = ref<Sort[]>()

const assetsBunch = computed(() => assetsBunchStore.getList)

const endOfDate = computed(() => repositoriesStore.getCurrentTimelineEnd)

const sm = '1fr 1fr'
const columns = computed(() => [
  {
    name: AnalyticsField.ASSET_ID,
    caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET],
    formatter: (assetId: string) => getAssetTicker(assetsBunch.value)(assetId),
    tooltip: (field: string, item?: AnalyticsPosition) => {
      const key = field as keyof AnalyticsPosition
      return item?.[key]
        ? getAssetName(assetsBunch.value, '')(item[key].toString())
        : undefined
    },
  },
  {
    name: AnalyticsField.AMOUNT,
    caption: ANALYTICS_CAPTIONS[AnalyticsField.AMOUNT],
    formatter: analyticsStore.amountFormatter,
    headerValueClasses: 'ui-grid__header-value--right',
    cellValueClasses: 'ui-grid__cell--right number-node',
    footerValueClasses: 'ui-grid__footer-value--right number-node',
  },
  {
    name: AnalyticsField.POSITION_CLOSE,
    caption: ANALYTICS_CAPTIONS[AnalyticsField.POSITION_CLOSE],
    formatter: analyticsStore.valueFormatter,
    headerValueClasses: 'ui-grid__header-value--right',
    cellValueClasses: 'ui-grid__cell--right number-node',
    footerValueClasses: 'ui-grid__footer-value--right number-node',
  },
  {
    name: AnalyticsField.POSITION_COST,
    caption: ANALYTICS_CAPTIONS[AnalyticsField.POSITION_COST],
    formatter: analyticsStore.valueFormatter,
    headerValueClasses: 'ui-grid__header-value--right',
    cellValueClasses: 'ui-grid__cell--right number-node',
    footerValueClasses: 'ui-grid__footer-value--right number-node',
  },
])

const isExportDisabled = computed(() => items.value.length === 0)

const footer = computed(() => ({
  [AnalyticsField.ASSET_ID]: 'Total',
  [AnalyticsField.POSITION_CLOSE]: analyticsStore.valueFormatter(sumBy(inputItems.value, AnalyticsField.POSITION_CLOSE)),
  [AnalyticsField.POSITION_COST]: analyticsStore.valueFormatter(sumBy(inputItems.value, AnalyticsField.POSITION_COST)),
}))

const amountFilter = ref<FilterGroup>({
  logic: FilterLogic.AND,
  params: [
    { comparison: FilterComparison.GREATER, field: AnalyticsField.AMOUNT, value: '0' },
  ],
})

const inputItems = computed(() => positions.value)
const filteredItems = useGridFilter(
  columns,
  inputItems,
  clearedSearch,
  amountFilter,
)
const items = useGridSort(sort, columns, filteredItems, ['position_close'])

const fetchPositions = async () => {
  try {
    const result = await analyticsStore.fetch({
      date: endOfDate.value,
      accounts: JSON.stringify([props.instance.id]),
      data: 'position',
    })
    positions.value = result.position_view
  } catch (e) {
    handleCatchedError(e as string)
  } finally {
    isLoading.value = false
  }
}

const handleClickExport = () => {
  const assetName = props.instance.field(ASSET_FIELD.NAME).value
  useHandledGridExport(columns.value, items.value, `${assetName}_positions`, true)
}

onMounted(fetchPositions)
</script>
